import { useRef, useState } from 'react';

const useDragger = (chartRef, setThreshold, numOfLabels) => {
  const [triggerRerender, setTriggerRerender] = useState(false);

  // Subtract 1 because of 0 based indexing and 0.5 to make the max value snap between the bars.
  const maxThreshold = Number(numOfLabels) - 1.5;

  const elementRef = useRef();
  const lastEventRef = useRef();

  const dragHorizontal = (moveX) => {
    // Get the chart instance to access canvas bounds
    const chart = chartRef.current;
    if (!chart || !chart.scales || !chart.scales.x) {
      return;
    }

    // Get the canvas bounds (left and right edges in pixels)
    const canvasLeft = chart.scales.x.left;
    const canvasRight = chart.scales.x.right;

    // Calculate the new position after the move
    const newX = elementRef.current.x + moveX;

    // Check if the new position would be outside the canvas bounds
    if (newX < canvasLeft || newX > canvasRight) {
      // Don't move if it would go outside the canvas
      return;
    }

    // Apply the movement if it's within bounds
    elementRef.current.x += moveX;
    elementRef.current.x2 += moveX;
    elementRef.current.centerX += moveX;

    if (elementRef.current.elements && elementRef.current.elements.length) {
      for (const subEl of elementRef.current.elements) {
        subEl.x += moveX;
        subEl.x2 += moveX;
        subEl.centerX += moveX;
        subEl.bX += moveX;
      }
    }
  };

  const handleElementDragging = (event) => {
    if (!lastEventRef.current || !elementRef.current) {
      return;
    }
    const moveX = event.x - lastEventRef.current.x;
    dragHorizontal(moveX);
    lastEventRef.current = event;
    return true;
  };

  const handleDrag = (event) => {
    if (elementRef.current && elementRef.current.options.id === 'threshold') {
      switch (event.type) {
        case 'mousemove':
          return handleElementDragging(event);
        case 'mouseout':
        case 'mouseup':
          lastEventRef.current = undefined;
          const chart = chartRef.current;
          if (chart && chart.scales && chart.scales.x) {
            const nearestXValue = chart.scales.x.getValueForPixel(
              elementRef.current.x
            );

            // Add 0.5 to the nearestXValue to make the line snap between the bars.
            const snappedValue = nearestXValue + 0.5;

            // Clamp the snappedValue between 0 and the max Value of the chart
            setThreshold(Math.max(0, Math.min(maxThreshold, snappedValue)));
            setTriggerRerender((p) => !p);
          }
          break;
        case 'mousedown':
          lastEventRef.current = event;
          break;
        default:
      }
    }
  };

  const handleEnter = (ctx) => {
    if (lastEventRef.current) {
      return;
    }
    elementRef.current = ctx.element;
    ctx.chart.canvas.style.cursor = 'pointer';
  };

  const handleLeave = (ctx) => {
    if (lastEventRef.current) {
      return;
    }
    elementRef.current = undefined;
    lastEventRef.current = undefined;
    ctx.chart.canvas.style.cursor = 'default';
  };

  const dragger = {
    id: 'dragger',
    beforeEvent(chart, args, options) {
      if (handleDrag(args.event)) {
        args.changed = true;
        return;
      }
    },
  };

  return {
    handleEnter,
    handleLeave,
    dragger,
    triggerRerender,
  };
};

export default useDragger;
